// src/finance/serviceRevenue.ts
// Composition des DEUX sources de revenu : la prestation (liée au temps facturé)
// et l'application (abonnements App Store, indépendants du temps).
//
// Apple est fournisseur réputé (art. 9 bis du règlement UE 282/2011) : il collecte
// les taxes de vente et prélève sa commission avant de reverser. La comptabilisation
// retenue est le BRUT — le chiffre d'affaires est ce que payent les utilisateurs, et
// la commission est une CHARGE d'exploitation. C'est la seule lecture cohérente avec
// le fait que le développeur fixe le prix et porte la responsabilité du service.
import type { CorporateRegime, FinanceSettings } from './engine/types'
import { simulateRegime } from './engine/engine'
import { round2 } from './engine/tax'

/** Commission Apple du mois, en euros. */
export function appCommissionOf(settings: FinanceSettings): number {
  return round2(settings.appRevenueGross * settings.appCommissionRate)
}

/** Ce qu'Apple verse réellement : le brut moins sa commission. */
export function appNetOf(settings: FinanceSettings): number {
  return round2(settings.appRevenueGross - appCommissionOf(settings))
}

export type ComposedRevenue = {
  /** CA total = prestation + brut de l'app (la commission n'en est PAS déduite). */
  revenueTotal: number
  /** Charges société = dépenses saisies + commission Apple. */
  expensesTotal: number
}

/**
 * Compose les deux sources pour le moteur, qui n'a pas à les connaître : il reçoit
 * un CA et des charges, comme avant.
 */
export function composeRevenue(
  settings: FinanceSettings,
  serviceRevenueMonthly: number,
  companyExpensesMonthly: number,
): ComposedRevenue {
  return {
    revenueTotal: round2(serviceRevenueMonthly + settings.appRevenueGross),
    expensesTotal: round2(companyExpensesMonthly + appCommissionOf(settings)),
  }
}

const TOLERANCE = 0.005
const MAX_ITERATIONS = 200

/**
 * CA de PRESTATION nécessaire pour atteindre le net visé, le revenu d'app étant
 * une donnée d'entrée. C'est la question réellement utile : « combien me reste-t-il
 * à facturer ce mois-ci ? » — et c'est la seule grandeur à laquelle un TJM ou un
 * CA horaire puisse s'appliquer.
 *
 * Rend `0` si l'app couvre déjà l'objectif (jamais un montant négatif), et `null`
 * si la cible est inatteignable avec ces paramètres fiscaux — même contrat que
 * `findRevenueForNet`, dont cette fonction reprend la bissection.
 */
export function findServiceRevenueForNet(
  targetNetMonthly: number,
  share: number,
  settings: FinanceSettings,
  companyExpensesMonthly: number,
  corporateRegime: CorporateRegime = 'uk',
): number | null {
  if (targetNetMonthly <= 0) return 0

  const netAt = (serviceRevenue: number): number => {
    const c = composeRevenue(settings, serviceRevenue, companyExpensesMonthly)
    return simulateRegime(c.revenueTotal, share, settings, c.expensesTotal, corporateRegime).netMonthly
  }

  // L'app seule suffit déjà : aucune prestation nécessaire. Sans ce test, la
  // bissection chercherait une racine sous zéro et rendrait un montant négatif.
  if (netAt(0) >= targetNetMonthly) return 0

  let lo = 0
  let hi = Math.max(targetNetMonthly, 1_000)
  let guard = 0
  while (netAt(hi) < targetNetMonthly && guard < 60) {
    hi *= 2
    guard += 1
  }
  // Doublements épuisés : aucun CA de prestation n'atteint la cible.
  if (netAt(hi) < targetNetMonthly) return null

  for (let i = 0; i < MAX_ITERATIONS; i += 1) {
    const mid = (lo + hi) / 2
    const net = netAt(mid)
    if (Math.abs(net - targetNetMonthly) < TOLERANCE) return round2(mid)
    if (net < targetNetMonthly) lo = mid
    else hi = mid
  }
  return round2((lo + hi) / 2)
}
